import { createIDTagging } from '@msdk/common'

import { FieldEntityPair } from './field-entity-pair'

export class NodeItem {
  public value: string
  public description: string
  public entityName: string
  public level: number
  public idml: string
  public microservice: string
  public tDescription: string

  constructor(
    value: string,
    description: string,
    entityName: string,
    level: number,
    idml: string,
    microservice: string,
    tDescription: string,
  ) {
    this.value = value
    this.description = description
    this.entityName = entityName
    this.level = level
    this.idml = idml
    this.microservice = microservice
    this.tDescription = tDescription
  }

  public toString(): string {
    return `${this.tDescription ?? this.description}`
  }

  public toIdTagging(): string {
    return createIDTagging(this.microservice, this.entityName, this.idml)
  }

  public toFieldEntityPair(): FieldEntityPair {
    return new FieldEntityPair(this.value, this.entityName)
  }

  public equals(item: FieldEntityPair | NodeItem) {
    let pair = item as FieldEntityPair
    if (item instanceof NodeItem) pair = item.toFieldEntityPair()
    return this.value === pair.fieldName && this.entityName === pair.entityName
  }

  public clone(): NodeItem {
    return new NodeItem(
      this.value,
      this.description,
      this.entityName,
      this.level,
      this.idml,
      this.microservice,
      this.tDescription,
    )
  }
}

export class NodeGroup extends NodeItem {
  public children: NodeItem[]

  private get spaces(): string {
    let res = ''
    for (let i = 0; i < this.level; i++) res += ' '
    return res
  }

  constructor(
    value: string,
    description: string,
    entityName: string,
    level: number,
    idml: string,
    microservice: string,
    tDescription: string,
  ) {
    super(value, description, entityName, level, idml, microservice, tDescription)
    this.children = []
  }

  public toString(): string {
    let res = `${this.tDescription || this.description || this.value}:`
    this.children.forEach((c, i) => {
      if (i === this.children.length - 1) res += `\n${this.spaces}${c.toString()}`
      else res += `\n${this.spaces}${c.toString()},`
    })
    return res
  }

  public toIdTagging(): string {
    let res = `${createIDTagging(this.microservice, this.entityName, this.idml)}:`
    this.children.forEach((c, i) => {
      if (i === this.children.length - 1) res += `\n${this.spaces}${c.toIdTagging()}`
      else res += `\n${this.spaces}${c.toIdTagging()},`
    })
    return res
  }

  public sort(): void {
    this.children.sort((a, b) => {
      if (a instanceof NodeGroup) a.sort()

      if (a.description < b.description) {
        return -1
      }
      if (a.description > b.description) {
        return 1
      }
      return 0
    })
  }

  public toFieldEntityPairs(): FieldEntityPair[] {
    let result: FieldEntityPair[] = []
    this.children.forEach((c) => {
      if (c instanceof NodeGroup) {
        const toAdd = c.toFieldEntityPairs()
        result = result.concat(toAdd)
      } else {
        result.push(c.toFieldEntityPair())
      }
    })
    return result
  }

  public allEquals(pairs: FieldEntityPair[]) {
    let res = true
    this.children.forEach((c) => {
      res &&= pairs.some((p) => c.equals(p))
    })
    return res
  }

  public someEquals(pairs: FieldEntityPair[]) {
    let res = false
    this.children.forEach((c) => {
      res ||= pairs.some((p) => c.equals(p))
    })
    return res && !this.allEquals(pairs)
  }

  public clone(): NodeGroup {
    const cloned = new NodeGroup(
      this.value,
      this.description,
      this.entityName,
      this.level,
      this.idml,
      this.microservice,
      this.tDescription,
    )
    this.children.forEach((c) => {
      const childCloned = c.clone()
      cloned.children.push(childCloned)
    })
    return cloned
  }
}
